import {type AudioEngine} from '#/lib/flashbookAudioEngine'

/** The scheme of a URL, or `undefined` when it is relative. */
function schemeOf(uri: string): string | undefined {
  return /^([a-z][a-z0-9+.-]*):/i.exec(uri)?.[1]?.toLowerCase()
}

/**
 * The browser's own audio engine.
 *
 * A thin adapter rather than a re-implementation: the desk's graphs run on the
 * real Web Audio API here, and the two platform-specific bits are handled by the
 * two APIs the web actually has.
 */
export function createPlatformEngine(): AudioEngine {
  return {
    available: () =>
      typeof AudioContext !== 'undefined' &&
      typeof OfflineAudioContext !== 'undefined',

    createContext: () => new AudioContext(),

    createOfflineContext: (channels, length, sampleRate) =>
      new OfflineAudioContext(channels, length, sampleRate),

    /*
     * A blob URL *is* how a browser hands bytes to a player, and it costs nothing
     * to mint: the browser keeps the bytes alive for exactly as long as the URL
     * does. `key` is unused here by design.
     */
    persistClip: async wav =>
      URL.createObjectURL(new Blob([wav], {type: 'audio/wav'})),

    releaseClip: url => {
      try {
        URL.revokeObjectURL(url)
      } catch {}
    },

    /*
     * `fetch` is how a browser reads a clip it did not create itself. It resolves
     * a `blob:` URL, and it cannot resolve a `file:` one at all — which is a true
     * answer about this platform, and the one a creator is better off hearing than
     * a decode error.
     */
    readLocalClip: async uri => {
      if (schemeOf(uri) === 'blob') {
        const response = await fetch(uri)
        if (!response.ok) {
          throw new Error(`the clip answered HTTP ${response.status}`)
        }
        return response.arrayBuffer()
      }
      throw new Error(
        `the desk cannot read a ${schemeOf(uri) ?? 'relative'} URI`,
      )
    },
  }
}
